import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { startOfDay, addDays, format } from 'date-fns';


const DEFAULT_PRICING = [
  { id: 'low', name: 'Low Season', start_month: 10, start_day: 1, end_month: 5, end_day: 31, rate: 400, min_stay: 3 },
  { id: 'mid', name: 'Mid Season', start_month: 6, start_day: 1, end_month: 6, end_day: 30, rate: 630, min_stay: 5 },
  { id: 'high', name: 'High Season', start_month: 7, start_day: 1, end_month: 7, end_day: 31, rate: 900, min_stay: 7 },
  { id: 'august', name: 'August Peak', start_month: 8, start_day: 1, end_month: 8, end_day: 31, rate: 1300, min_stay: 7 },
  { id: 'shoulder', name: 'Shoulder', start_month: 9, start_day: 1, end_month: 9, end_day: 30, rate: 540, min_stay: 5 },
];

function getBlockedDates() {
  try {
    return JSON.parse(localStorage.getItem('blockedDates')) || { manual: [], ical: [] };
  } catch { return { manual: [], ical: [] }; }
}

function setBlockedDates(data) {
  localStorage.setItem('blockedDates', JSON.stringify(data));
}

function getPricingFromStorage() {
  try {
    const stored = JSON.parse(localStorage.getItem('pricingSeasons'));
    return stored && stored.length > 0 ? stored : DEFAULT_PRICING;
  } catch { return DEFAULT_PRICING; }
}

const AvailabilityContext = createContext();

export const AvailabilityProvider = ({ children }) => {
  const [blockedData, setBlockedData] = useState(getBlockedDates());
  const [lastSync, setLastSync] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pricingSeasons, setPricingSeasons] = useState(getPricingFromStorage());

  const allBlockedDates = [...new Set([...(blockedData.manual || []), ...(blockedData.ical || [])])];

  const fetchIcal = useCallback(async () => {
    try {
      const res = await fetch('/api/ical');
      if (!res.ok) throw new Error('iCal fetch failed');
      const data = await res.json();
      const icalDates = data.dates || [];
      const current = getBlockedDates();
      const updated = { ...current, ical: icalDates };
      setBlockedDates(updated);
      setBlockedData(updated);
      if (data.sync?.last_success) setLastSync(data.sync.last_success);
    } catch (err) {
      console.error('iCal sync error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchIcal();
  }, [fetchIcal]);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'blockedDates') {
        setBlockedData(getBlockedDates());
      }
      if (e.key === 'pricingSeasons') {
        setPricingSeasons(getPricingFromStorage());
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const refreshFromStorage = useCallback(() => {
    setBlockedData(getBlockedDates());
    setPricingSeasons(getPricingFromStorage());
  }, []);

  const isDateBooked = useCallback((date) => {
    const dateStr = typeof date === 'string' ? date : format(startOfDay(date), 'yyyy-MM-dd');
    return allBlockedDates.includes(dateStr);
  }, [allBlockedDates]);

  const isRangeOverlapping = useCallback((checkInStr, checkOutStr) => {
    if (!checkInStr || !checkOutStr) return false;
    const checkIn = startOfDay(new Date(checkInStr));
    const checkOut = startOfDay(new Date(checkOutStr));
    let current = checkIn;
    while (current < checkOut) {
      const dateStr = format(current, 'yyyy-MM-dd');
      if (allBlockedDates.includes(dateStr)) return true;
      current = addDays(current, 1);
    }
    return false;
  }, [allBlockedDates]);

  const getPriceForDate = useCallback((date) => {
    const month = date.getMonth() + 1;
    const day = date.getDate();
    for (const season of pricingSeasons) {
      const sm = season.start_month;
      const sd = season.start_day;
      const em = season.end_month;
      const ed = season.end_day;
      let inSeason;
      if (sm <= em) {
        inSeason = (month > sm || (month === sm && day >= sd)) && (month < em || (month === em && day <= ed));
      } else {
        inSeason = (month > sm || (month === sm && day >= sd)) || (month < em || (month === em && day <= ed));
      }
      if (inSeason) return season.rate;
    }
    return 400;
  }, [pricingSeasons]);

  return (
    <AvailabilityContext.Provider value={{
      blockedRanges: allBlockedDates,
      blockedData,
      lastSync,
      loading,
      pricingSeasons,
      isDateBooked,
      isRangeOverlapping,
      getPriceForDate,
      refetch: fetchIcal,
      refreshFromStorage,
    }}>
      {children}
    </AvailabilityContext.Provider>
  );
};

export const useAvailability = () => {
  const context = useContext(AvailabilityContext);
  if (!context) throw new Error('useAvailability must be used within AvailabilityProvider');
  return context;
};
